/* ============================================================
   Art by location — bespoke map finder (no dependencies, no Maps API)
   Equirectangular projection. Land drawn from a simplified world asset.
   Markers clustered in screen space and synced with the filter toolbar
   and the result grid. The grid/list is the accessible equivalent of the map.
   ============================================================ */
(function () {
  'use strict';

  var SVGNS = 'http://www.w3.org/2000/svg';
  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function el(tag, attrs, parent) {
    var n = document.createElementNS(SVGNS, tag);
    if (attrs) for (var k in attrs) n.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(n);
    return n;
  }
  function money(amount, cur) {
    var sym = { GBP: '£', USD: '$', EUR: '€' }[cur] || '';
    return sym + amount;
  }
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
  function debounceRaf(fn) {
    var scheduled = false;
    return function () { if (scheduled) return; scheduled = true; requestAnimationFrame(function () { scheduled = false; fn(); }); };
  }

  // Web-Mercator projection (the standard for web maps). A single equirectangular
  // "standard parallel" can make the UK look right OR the world look right, but not
  // both. Mercator is conformal at every latitude, so shapes stay correct whether the
  // camera is on Britain or the whole globe. The vertical scale is tuned so that at
  // the equator 1 unit ≈ 1° (matching x), which keeps the camera/clustering maths the
  // same as before. Everything (land, graticule, markers, camera) flows through px/py,
  // so swapping the projection here is consistent across the whole map.
  var MERC_K = 180 / Math.PI;                                // ~57.296 — equator y-scale ≈ x-scale
  function px(lng) { return lng + 180; }
  function py(lat) {
    var L = Math.max(-85, Math.min(85, lat));                // clamp the unprojectable poles
    return 180 - MERC_K * Math.log(Math.tan(Math.PI / 4 + L * Math.PI / 360));
  }

  var UK_BOX = { minLng: -8.8, maxLng: 2.2, minLat: 49.8, maxLat: 61.2 };

  function ArtMap(root) {
    this.root = root;
    this.mapEl = root.querySelector('[data-abl-map]');
    this.gridEl = root.querySelector('[data-abl-grid]');
    this.countEl = root.querySelector('[data-abl-count]');
    this.emptyEl = root.querySelector('[data-abl-empty]');
    this.activeEl = root.querySelector('[data-abl-active]');
    this.activeLabelEl = root.querySelector('[data-abl-active-label]');
    this.loadingEl = root.querySelector('[data-abl-loading]');
    this.searchEl = root.querySelector('[data-abl-search]');
    this.regionField = root.querySelector('[data-abl-region-field]');
    this.resetEl = root.querySelector('[data-abl-reset]');
    this.filtersEl = root.querySelector('[data-abl-filters]');
    this.filtersToggleEl = root.querySelector('[data-abl-filters-toggle]');
    this.filtersToggleLabelEl = root.querySelector('[data-abl-filters-toggle-label]');
    this.chipsEl = root.querySelector('[data-abl-chips]');
    this.statsEl = root.querySelector('[data-abl-stats]');
    this.sheetHandleEl = root.querySelector('[data-abl-sheet-handle]');
    this.sheetCountEl = root.querySelector('[data-abl-sheet-count]');
    this.filters = {};
    var self = this;
    Array.prototype.forEach.call(root.querySelectorAll('[data-abl-filter]'), function (s) {
      self.filters[s.getAttribute('data-abl-filter')] = s;
    });
    this.cam = { s: 1, tx: 0, ty: 0 };
    this.selected = null;      // {label, handles:Set}
    this.items = [];
    this.places = [];
    this.scheduleMarkers = debounceRaf(this.renderMarkers.bind(this));
    this.init();
  }

  ArtMap.prototype.init = function () {
    var self = this;
    var dsUrl = this.root.getAttribute('data-dataset');
    var bmUrl = this.root.getAttribute('data-basemap');
    Promise.all([
      fetch(dsUrl).then(function (r) { return r.json(); }),
      fetch(bmUrl).then(function (r) { return r.json(); }).catch(function () { return []; })
    ]).then(function (res) {
      self.items = res[0] || [];
      self.basemap = res[1] || [];
      self.build();
    }).catch(function () {
      if (self.loadingEl) self.loadingEl.textContent = 'The map could not load. Browse the collections instead.';
    });
  };

  ArtMap.prototype.build = function () {
    this.buildPlaces();
    this.buildSvg();
    this.tip = document.createElement('div');
    this.tip.className = 'abl__tip';
    this.mapEl.appendChild(this.tip);
    this.populateFilters();
    this.buildStats();
    this.bindControls();
    this.setView('uk', false);
    this.apply();
    if (this.loadingEl) this.loadingEl.remove();
  };

  // Verified-scale stat line: numbers come straight from the dataset so they never drift.
  ArtMap.prototype.buildStats = function () {
    if (!this.statsEl) return;
    var self = this;
    var prints = this.items.length;
    var places = (function () { var s = {}; self.items.forEach(function (it) { if (it.loc) s[it.loc] = 1; }); return Object.keys(s).length; })();
    var countries = (function () { var s = {}; self.items.forEach(function (it) { if (it.c) s[it.c] = 1; }); return Object.keys(s).length; })();
    var targets = { prints: prints, places: places, countries: countries };
    var nodes = {};
    Array.prototype.forEach.call(this.statsEl.querySelectorAll('[data-abl-stat]'), function (n) { nodes[n.getAttribute('data-abl-stat')] = n; });
    function setFinal() { Object.keys(targets).forEach(function (k) { if (nodes[k]) nodes[k].textContent = targets[k]; }); }
    if (REDUCE || !('IntersectionObserver' in window)) { setFinal(); return; }
    var ran = false;
    var io = new IntersectionObserver(function (entries) {
      if (ran || !entries[0].isIntersecting) return;
      ran = true; io.disconnect();
      var t0 = null, dur = 650;
      function frame(t) {
        if (t0 === null) t0 = t;
        var k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 3);
        Object.keys(targets).forEach(function (key) { if (nodes[key]) nodes[key].textContent = Math.round(targets[key] * e); });
        if (k < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }, { threshold: 0.4 });
    Object.keys(nodes).forEach(function (k) { nodes[k].textContent = '0'; });
    io.observe(this.statsEl);
  };
  ArtMap.prototype.showTip = function (x, y, text) {
    if (!this.tip) return;
    this.tip.textContent = text;
    this.tip.style.left = x + 'px';
    this.tip.style.top = y + 'px';
    this.tip.classList.add('is-on');
  };
  ArtMap.prototype.hideTip = function () { if (this.tip) this.tip.classList.remove('is-on'); };

  // group items into distinct places (by ~1km rounded coord)
  ArtMap.prototype.buildPlaces = function () {
    var map = {};
    this.items.forEach(function (it) {
      if (typeof it.lat !== 'number' || typeof it.lng !== 'number') return;
      var key = it.lat.toFixed(2) + ',' + it.lng.toFixed(2);
      if (!map[key]) map[key] = { lat: it.lat, lng: it.lng, label: it.loc, country: it.c, items: [] };
      map[key].items.push(it);
    });
    this.places = Object.keys(map).map(function (k) { return map[k]; });
  };

  ArtMap.prototype.buildSvg = function () {
    var svg = el('svg', { preserveAspectRatio: 'xMidYMid slice' }, this.mapEl);
    this.svg = svg;
    // ink stipple pattern (paper tooth over the land) — fill styled via CSS class
    var defs = el('defs', null, svg);
    var pat = el('pattern', { id: 'abl-stipple', patternUnits: 'userSpaceOnUse', width: 3, height: 3 }, defs);
    el('circle', { class: 'abl-stipple-dot', cx: 1.5, cy: 1.5, r: 0.35 }, pat);
    this.landG = el('g', { class: 'abl-lands' }, svg);
    // graticule (drawn behind land) + stronger equator/prime-meridian reference
    var gd = '';
    for (var lng = -160; lng <= 160; lng += 20) gd += 'M' + px(lng) + ' ' + py(83) + 'V' + py(-83);
    for (var lat = -80; lat <= 80; lat += 20) gd += 'M' + px(-180) + ' ' + py(lat) + 'H' + px(180);
    el('path', { class: 'abl-grat', d: gd }, this.landG);
    var gdRef = 'M' + px(0) + ' ' + py(83) + 'V' + py(-83) + 'M' + px(-180) + ' ' + py(0) + 'H' + px(180);
    el('path', { class: 'abl-grat abl-grat--ref', d: gdRef }, this.landG);
    // land — rings are flat coordinate arrays [x0,y0,x1,y1,...]; drawn 3x (shelf, fill, stipple tint)
    var d = '';
    this.basemap.forEach(function (ring) {
      for (var i = 0; i < ring.length; i += 2) d += (i === 0 ? 'M' : 'L') + px(ring[i]).toFixed(2) + ' ' + py(ring[i + 1]).toFixed(2);
      d += 'Z';
    });
    el('path', { class: 'abl-land--shelf', d: d, transform: 'translate(0,1.5)' }, this.landG);
    el('path', { class: 'abl-land', d: d }, this.landG);
    el('path', { class: 'abl-land--tint', d: d }, this.landG);
    this.markerG = el('g', { class: 'abl-markers' }, svg);
    this.sizeSvg();
  };

  ArtMap.prototype.sizeSvg = function () {
    var r = this.mapEl.getBoundingClientRect();
    this.w = r.width; this.h = r.height;
    this.svg.setAttribute('viewBox', '0 0 ' + this.w + ' ' + this.h);
    this.svg.setAttribute('width', this.w);
    this.svg.setAttribute('height', this.h);
  };

  // ---- camera ----
  ArtMap.prototype.fitScale = function (box) {
    var x0 = px(box.minLng), x1 = px(box.maxLng), y0 = py(box.maxLat), y1 = py(box.minLat);
    var pad = 28;
    return Math.min((this.w - 2 * pad) / (x1 - x0), (this.h - 2 * pad) / (y1 - y0));
  };
  ArtMap.prototype.setView = function (which, animate) {
    var box = which === 'world' ? { minLng: -170, maxLng: 190, minLat: -56, maxLat: 80 } : UK_BOX;
    var s = this.fitScale(box);
    var cx = (px(box.minLng) + px(box.maxLng)) / 2;
    var cy = (py(box.maxLat) + py(box.minLat)) / 2;
    this.view = which;
    this.animateTo({ s: s, tx: this.w / 2 - s * cx, ty: this.h / 2 - s * cy }, animate);
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-abl-view]'), function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-abl-view') === which);
    });
  };
  ArtMap.prototype.worldFit = function () { return this.fitScale({ minLng: -179, maxLng: 200, minLat: -58, maxLat: 84 }); };
  ArtMap.prototype.clampCam = function () {
    var sMin = this.worldFit() * 0.92, sMax = 70;
    if (this.cam.s < sMin) this.cam.s = sMin;
    if (this.cam.s > sMax) this.cam.s = sMax;
  };
  ArtMap.prototype.applyCam = function () {
    this.clampCam();
    this.landG.setAttribute('transform', 'translate(' + this.cam.tx + ',' + this.cam.ty + ') scale(' + this.cam.s + ')');
    this.scheduleMarkers();
  };
  ArtMap.prototype.animateTo = function (target, animate) {
    var self = this;
    if (!animate || REDUCE) { this.cam = target; this.applyCam(); return; }
    var start = { s: this.cam.s, tx: this.cam.tx, ty: this.cam.ty }, t0 = null, dur = 480;
    function frame(t) {
      if (t0 === null) t0 = t;
      var k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 3);
      self.cam = { s: start.s + (target.s - start.s) * e, tx: start.tx + (target.tx - start.tx) * e, ty: start.ty + (target.ty - start.ty) * e };
      self.applyCam();
      if (k < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  };
  ArtMap.prototype.zoomAround = function (factor, cx, cy) {
    var ns = this.cam.s * factor;
    var sMin = this.worldFit() * 0.92, sMax = 70;
    ns = Math.max(sMin, Math.min(sMax, ns));
    this.cam.tx = cx - (cx - this.cam.tx) * (ns / this.cam.s);
    this.cam.ty = cy - (cy - this.cam.ty) * (ns / this.cam.s);
    this.cam.s = ns;
    this.applyCam();
  };
  ArtMap.prototype.screenOf = function (lng, lat) {
    return { x: px(lng) * this.cam.s + this.cam.tx, y: py(lat) * this.cam.s + this.cam.ty };
  };

  // ---- controls ----
  ArtMap.prototype.bindControls = function () {
    var self = this, m = this.mapEl;
    // drag pan — capture is DEFERRED until a real drag starts, so a plain click
    // still reaches the pins + control buttons (capturing on pointerdown swallowed them).
    var dragging = false, moved = false, captured = false, lx = 0, ly = 0;
    m.addEventListener('pointerdown', function (e) {
      if (e.target.closest && e.target.closest('.abl__map-controls')) return; // let controls handle their own clicks
      dragging = true; moved = false; captured = false; lx = e.clientX; ly = e.clientY;
    });
    m.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - lx, dy = e.clientY - ly;
      if (!moved && Math.abs(dx) + Math.abs(dy) > 4) {
        moved = true; m.classList.add('is-grabbing');
        try { m.setPointerCapture(e.pointerId); captured = true; } catch (_) {}
      }
      if (!moved) return;
      self.cam.tx += dx; self.cam.ty += dy; lx = e.clientX; ly = e.clientY;
      self.applyCam();
    });
    function endDrag(e) {
      if (captured && e && e.pointerId != null) { try { m.releasePointerCapture(e.pointerId); } catch (_) {} }
      dragging = false; captured = false; m.classList.remove('is-grabbing');
    }
    m.addEventListener('pointerup', endDrag);
    m.addEventListener('pointercancel', endDrag);
    m._moved = function () { return moved; };
    // wheel zoom
    m.addEventListener('wheel', function (e) {
      e.preventDefault();
      var r = m.getBoundingClientRect();
      self.zoomAround(e.deltaY < 0 ? 1.18 : 1 / 1.18, e.clientX - r.left, e.clientY - r.top);
    }, { passive: false });
    // pinch zoom
    var pts = {}, pinchD = 0;
    m.addEventListener('pointerdown', function (e) { pts[e.pointerId] = e; });
    m.addEventListener('pointermove', function (e) {
      if (!(e.pointerId in pts)) return; pts[e.pointerId] = e;
      var ids = Object.keys(pts);
      if (ids.length === 2) {
        dragging = false;
        var a = pts[ids[0]], b = pts[ids[1]];
        var d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        var r = m.getBoundingClientRect();
        var cx = (a.clientX + b.clientX) / 2 - r.left, cy = (a.clientY + b.clientY) / 2 - r.top;
        if (pinchD) self.zoomAround(d / pinchD, cx, cy);
        pinchD = d;
      }
    });
    function clearPt(e) { delete pts[e.pointerId]; if (Object.keys(pts).length < 2) pinchD = 0; }
    m.addEventListener('pointerup', clearPt);
    m.addEventListener('pointercancel', clearPt);
    // zoom + view buttons
    this.root.querySelector('[data-abl-zoom="in"]').addEventListener('click', function () { self.zoomAround(1.5, self.w / 2, self.h / 2); });
    this.root.querySelector('[data-abl-zoom="out"]').addEventListener('click', function () { self.zoomAround(1 / 1.5, self.w / 2, self.h / 2); });
    this.root.querySelector('[data-abl-zoom="reset"]').addEventListener('click', function () { self.setView(self.view || 'uk', true); });
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-abl-view]'), function (b) {
      b.addEventListener('click', function () { self.setView(b.getAttribute('data-abl-view'), true); });
    });
    // filters
    Object.keys(this.filters).forEach(function (key) {
      self.filters[key].addEventListener('change', function () { self.onFilterChange(key); });
    });
    if (this.searchEl) this.searchEl.addEventListener('input', debounceRaf(function () { self.apply(); }));
    if (this.resetEl) this.resetEl.addEventListener('click', function () { self.resetFilters(); });
    var ac = this.root.querySelector('[data-abl-active-clear]');
    if (ac) ac.addEventListener('click', function () { self.clearSelection(); });
    // filters toggle (mobile) — open/close the collapsible filter set
    if (this.filtersToggleEl) {
      this.filtersToggleEl.addEventListener('click', function () {
        var open = self.filtersEl.classList.toggle('is-open');
        self.filtersToggleEl.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    // quick-start chips — one-tap entry points
    if (this.chipsEl) {
      Array.prototype.forEach.call(this.chipsEl.querySelectorAll('[data-abl-chip-locs],[data-abl-chip-family]'), function (chip) {
        chip.addEventListener('click', function () { self.runChip(chip); });
      });
    }
    // empty-state recovery
    var ec = this.root.querySelector('[data-abl-empty-clear]');
    if (ec) ec.addEventListener('click', function () { self.resetFilters(); });
    var ea = this.root.querySelector('[data-abl-empty-all]');
    if (ea) ea.addEventListener('click', function () { self.resetFilters(); });
    // mobile sticky results button — scrolls to the results head
    if (this.sheetHandleEl) {
      this.sheetHandleEl.addEventListener('click', function () {
        var head = self.root.querySelector('.abl__results-head');
        if (head) head.scrollIntoView({ behavior: REDUCE ? 'auto' : 'smooth', block: 'start' });
      });
    }
    // resize
    window.addEventListener('resize', debounceRaf(function () {
      self.sizeSvg(); self.setView(self.view || 'uk', false);
    }));
  };

  // A quick-start chip either sets the Type filter (family) or flies to a place set.
  ArtMap.prototype.runChip = function (chip) {
    if (!this.items.length) return;
    var fam = chip.getAttribute('data-abl-chip-family');
    if (fam) {
      this.resetFilters();
      this.filters.family.value = fam;
      this.onFilterChange('family');
      return;
    }
    var key = (chip.getAttribute('data-abl-chip-locs') || '').toLowerCase();
    var not = (chip.getAttribute('data-abl-chip-not') || '').toLowerCase();
    if (!key) return;
    var hits = this.items.filter(function (it) {
      var loc = (it.loc || '').toLowerCase();
      if (loc.indexOf(key) === -1) return false;
      if (not && loc.indexOf(not) !== -1) return false;
      return true;
    });
    if (!hits.length) return;
    var label = chip.textContent.trim();
    var handles = new Set(hits.map(function (it) { return it.h; }));
    this.fitToItems(hits, true);
    this.selectPlace(label, handles, hits.length);
  };

  ArtMap.prototype.onFilterChange = function (key) {
    if (key === 'family') {
      var fam = this.filters.family.value;
      if (this.regionField) this.regionField.hidden = !(fam === 'original');
      if (fam === 'classical') this.setView('world', true);
      else if (fam === 'original') this.setView('uk', true);
    }
    if (key === 'country') {
      var cval = this.filters.country.value;
      var uk = cval === 'United Kingdom';
      if (this.regionField) this.regionField.hidden = !(uk || this.filters.family.value === 'original');
      if (cval) {
        var inC = this.items.filter(function (it) { return it.c === cval; });
        this.fitToItems(inC, true);
      } else {
        this.setView(this.filters.family.value === 'classical' ? 'world' : 'uk', true);
      }
    }
    this.clearSelection(true);
    this.apply();
  };

  // fit the camera to a set of items (used when a country is chosen)
  ArtMap.prototype.fitToItems = function (items, animate) {
    if (!items || !items.length) return;
    var minLng = 180, maxLng = -180, minLat = 90, maxLat = -90;
    items.forEach(function (it) {
      if (it.lng < minLng) minLng = it.lng; if (it.lng > maxLng) maxLng = it.lng;
      if (it.lat < minLat) minLat = it.lat; if (it.lat > maxLat) maxLat = it.lat;
    });
    var padLng = Math.max(1.2, (maxLng - minLng) * 0.25), padLat = Math.max(1.2, (maxLat - minLat) * 0.25);
    var box = { minLng: minLng - padLng, maxLng: maxLng + padLng, minLat: minLat - padLat, maxLat: maxLat + padLat };
    var s = Math.min(70, Math.max(this.worldFit() * 0.92, this.fitScale(box)));
    var cx = (px(box.minLng) + px(box.maxLng)) / 2, cy = (py(box.maxLat) + py(box.minLat)) / 2;
    this.animateTo({ s: s, tx: this.w / 2 - s * cx, ty: this.h / 2 - s * cy }, animate);
  };

  ArtMap.prototype.resetFilters = function () {
    var self = this;
    Object.keys(this.filters).forEach(function (k) { self.filters[k].value = ''; });
    if (this.searchEl) this.searchEl.value = '';
    if (this.regionField) this.regionField.hidden = true;
    this.clearSelection(true);
    this.apply();
  };

  // ---- filter options ----
  ArtMap.prototype.populateFilters = function () {
    var countries = {}, movements = {}, regions = {};
    this.items.forEach(function (it) {
      if (it.c) countries[it.c] = (countries[it.c] || 0) + 1;
      if (it.mv) movements[it.mv] = (movements[it.mv] || 0) + 1;
      if (it.reg) regions[it.reg] = (regions[it.reg] || 0) + 1;
    });
    function fill(sel, counts) {
      if (!sel) return;
      Object.keys(counts).sort().forEach(function (name) {
        var o = document.createElement('option');
        o.value = name; o.textContent = name + ' (' + counts[name] + ')';
        sel.appendChild(o);
      });
    }
    fill(this.filters.country, countries);
    fill(this.filters.movement, movements);
    fill(this.filters.region, regions);
  };

  // ---- core apply: filter -> markers + grid ----
  ArtMap.prototype.matches = function (it) {
    var f = this.filters, q = this.searchEl ? this.searchEl.value.trim().toLowerCase() : '';
    if (f.family && f.family.value && it.fam !== f.family.value) return false;
    if (f.country && f.country.value && it.c !== f.country.value) return false;
    if (f.region && f.region.value && it.reg !== f.region.value) return false;
    if (f.movement && f.movement.value && it.mv !== f.movement.value) return false;
    if (f.orientation && f.orientation.value && it.o !== f.orientation.value) return false;
    if (q) {
      var hay = (it.t + ' ' + it.a + ' ' + it.loc + ' ' + it.c).toLowerCase();
      if (hay.indexOf(q) === -1) return false;
    }
    return true;
  };

  ArtMap.prototype.apply = function () {
    var self = this;
    this.filtered = this.items.filter(function (it) { return self.matches(it); });
    var anyFilter = (this.searchEl && this.searchEl.value) || Object.keys(this.filters).some(function (k) { return self.filters[k].value; });
    if (this.resetEl) this.resetEl.hidden = !anyFilter;
    // reflect active filter count on the mobile toggle label
    if (this.filtersToggleLabelEl) {
      var active = Object.keys(this.filters).filter(function (k) { return self.filters[k].value; }).length;
      this.filtersToggleLabelEl.textContent = active ? 'Filters (' + active + ')' : 'Filters';
    }
    this.renderMarkers();
    this.renderGrid();
  };

  ArtMap.prototype.renderGrid = function () {
    var self = this;
    var list = this.filtered;
    if (this.selected) list = list.filter(function (it) { return self.selected.handles.has(it.h); });
    // count + active chip
    var n = list.length;
    if (this.selected) {
      this.activeEl.hidden = false;
      this.activeLabelEl.textContent = this.selected.label;
      this.countEl.textContent = n + (n === 1 ? ' print' : ' prints') + ' here';
    } else {
      this.activeEl.hidden = true;
      var anyFilter = (this.searchEl && this.searchEl.value) || Object.keys(this.filters).some(function (k) { return self.filters[k].value; });
      this.countEl.textContent = anyFilter
        ? (n + (n === 1 ? ' print' : ' prints'))
        : 'Tap a place on the map, or search a town you love. ' + n + ' prints to explore.';
    }
    this.emptyEl.hidden = n > 0;
    // mobile sticky button — keep a live count so the payoff feels one tap away
    if (this.sheetCountEl) {
      this.sheetCountEl.textContent = n === 0
        ? 'No prints here, adjust the map'
        : 'View ' + n + (n === 1 ? ' print' : ' prints') + (this.selected ? ' here' : '');
    }
    // build cards
    var frag = document.createDocumentFragment();
    list.slice(0, 120).forEach(function (it, i) { frag.appendChild(self.card(it, i)); });
    this.gridEl.innerHTML = '';
    this.gridEl.appendChild(frag);
  };

  ArtMap.prototype.card = function (it, i) {
    var li = document.createElement('li');
    li.className = 'abl-card';
    li.style.setProperty('--abl-delay', Math.min(i, 12) * 26 + 'ms');
    var alt = esc(it.t).replace(/"/g, '&quot;');
    li.innerHTML =
      '<a class="abl-card__link" href="/products/' + encodeURIComponent(it.h) + '">' +
        '<span class="abl-card__media">' +
          (it.img ? '<img src="' + it.img + '" alt="' + alt + '" loading="lazy" decoding="async">' : '') +
        '</span>' +
        '<span class="abl-card__title">' + esc(it.t) + '</span>' +
        (it.fam === 'classical' && it.a ? '<span class="abl-card__artist">' + esc(it.a) + '</span>' : '') +
        (it.loc ? '<span class="abl-card__place">' + esc(it.loc) + '</span>' : '') +
        (it.price ? '<span class="abl-card__price">from ' + money(it.price, it.cur) + '</span>' : '') +
        '<span class="abl-card__cta">View print</span>' +
      '</a>';
    return li;
  };

  // ---- markers (screen-space clustering) ----
  ArtMap.prototype.renderMarkers = function () {
    if (!this.markerG) return;
    var self = this;
    this.hideTip();
    // count filtered items per place
    var fset = {};
    (this.filtered || this.items).forEach(function (it) { fset[it.h] = true; });
    var visible = [];
    this.places.forEach(function (p) {
      var items = p.items.filter(function (it) { return fset[it.h]; });
      if (!items.length) return;
      var sc = self.screenOf(p.lng, p.lat);
      if (sc.x < -40 || sc.x > self.w + 40 || sc.y < -40 || sc.y > self.h + 40) return;
      visible.push({ x: sc.x, y: sc.y, items: items, label: p.label });
    });
    // greedy screen-space cluster
    visible.sort(function (a, b) { return b.items.length - a.items.length; });
    var R = 24, clusters = [];
    visible.forEach(function (p) {
      for (var i = 0; i < clusters.length; i++) {
        var c = clusters[i];
        if (Math.hypot(c.x - p.x, c.y - p.y) < R) {
          c.items = c.items.concat(p.items); c.places++;
          return;
        }
      }
      clusters.push({ x: p.x, y: p.y, items: p.items.slice(), label: p.label, places: 1 });
    });
    // render
    this.markerG.innerHTML = '';
    clusters.forEach(function (c) { self.drawMarker(c); });
  };

  ArtMap.prototype.drawMarker = function (c) {
    var self = this;
    var total = c.items.length;
    var handles = new Set(c.items.map(function (it) { return it.h; }));
    var isSel = this.selected && c.items.some(function (it) { return self.selected.handles.has(it.h); });
    var allPainted = c.items.every(function (it) { return it.k === 'painted'; });
    var cls = 'abl-pin' + (total > 1 ? ' abl-cluster' : '') + (allPainted ? ' abl-pin--painted' : '') + (isSel ? ' is-active' : '');
    var g = el('g', { class: cls, tabindex: '0', role: 'button' }, this.markerG);
    var tipText = total > 1 ? total + ' prints · ' + (c.label || 'this area') : (c.items[0].t + ' · ' + (c.label || ''));
    g.setAttribute('aria-label', total > 1 ? total + ' prints near ' + (c.label || 'this area') : (c.items[0].t + ', ' + (c.label || '')));
    if (total > 1) {
      var rad = 11 + Math.min(6, Math.round(Math.sqrt(total) * 1.1));
      el('circle', { class: 'abl-cluster__shadow', cx: c.x + 0.5, cy: c.y + 2, r: rad }, g);
      el('circle', { class: 'abl-cluster__focus', cx: c.x, cy: c.y, r: rad + 4 }, g);
      el('circle', { class: 'abl-cluster__bg', cx: c.x, cy: c.y, r: rad }, g);
      el('circle', { class: 'abl-cluster__inner', cx: c.x, cy: c.y, r: rad - 3.5 }, g);
      var tx = el('text', { class: 'abl-cluster__label', x: c.x, y: c.y }, g);
      tx.textContent = total > 99 ? '99+' : total;
    } else {
      el('circle', { class: 'abl-pin__shadow', cx: c.x + 0.5, cy: c.y + 1.5, r: 3 }, g);
      el('circle', { class: 'abl-pin__focus', cx: c.x, cy: c.y, r: 7 }, g);
      el('circle', { class: 'abl-pin__halo', cx: c.x, cy: c.y, r: 5.5 }, g);
      el('circle', { class: 'abl-pin__dot', cx: c.x, cy: c.y, r: 3 }, g);
    }
    function activate(e) {
      if (e) e.stopPropagation();
      if (self.mapEl._moved && self.mapEl._moved()) return; // ignore click after a drag
      self.hideTip();
      self.selectPlace(c.label, handles, total);
    }
    g.addEventListener('click', activate);
    g.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.hideTip(); self.selectPlace(c.label, handles, total); }
    });
    g.addEventListener('mouseenter', function () { self.showTip(c.x, c.y, tipText); });
    g.addEventListener('mouseleave', function () { self.hideTip(); });
    g.addEventListener('focus', function () { self.showTip(c.x, c.y, tipText); });
    g.addEventListener('blur', function () { self.hideTip(); });
  };

  ArtMap.prototype.selectPlace = function (label, handles, total) {
    this.selected = { label: (total > 1 ? total + ' prints near ' + (label || 'here') : label), handles: handles };
    this.renderGrid();
    this.renderMarkers();
    // bring results into view on mobile
    if (window.innerWidth < 990 && this.gridEl) {
      var top = this.root.querySelector('.abl__results-head');
      if (top) top.scrollIntoView({ behavior: REDUCE ? 'auto' : 'smooth', block: 'start' });
    }
  };
  ArtMap.prototype.clearSelection = function (silent) {
    this.selected = null;
    if (!silent) { this.renderGrid(); this.renderMarkers(); }
  };

  document.querySelectorAll('[data-abl]').forEach(function (root) { new ArtMap(root); });
})();
